import ExcelJS from 'exceljs';
import { Service } from '@/entities/service';
import { parseSafeNumber } from '@/shared/lib/number';

const UNIT_MAP: Record<string, string> = {
    'шт': 'pcs',
    'шт.': 'pcs',
    'pcs': 'pcs',
    'м2': 'm2_wall',
    'м2 стен': 'm2_wall',
    'm2_wall': 'm2_wall',
    'м2 пола': 'm2_floor',
    'm2_floor': 'm2_floor',
    'мп': 'mp',
    'м.п.': 'mp',
    'пог.м': 'mp',
    'mp': 'mp'
};

const REVERSE_UNIT_MAP: Record<string, string> = {
    'pcs': 'шт',
    'm2_wall': 'м2 стен',
    'm2_floor': 'м2 пола',
    'mp': 'м.п.'
};

export const servicesToExcel = async (services: Service[]): Promise<Blob> => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Прайс-лист');

    worksheet.columns = [
        { key: 'name', header: 'Название', width: 40 },
        { key: 'category', header: 'Категория', width: 20 },
        { key: 'unit', header: 'Ед. изм.', width: 10 },
        { key: 'price', header: 'Цена', width: 10 },
    ];

    services.forEach((s) => {
        worksheet.addRow({
            name: s.name,
            category: s.categoryId,
            unit: REVERSE_UNIT_MAP[s.unitType || s.unit] || s.unitType || s.unit,
            price: s.priceLabor,
        });
    });

    const buffer = await workbook.xlsx.writeBuffer();
    return new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
};

interface ImportedServiceData {
    name: string;
    category: string;
    unitType: string;
    priceLabor: number;
    adjustment: number;
}

export const parseExcelServices = async (file: File): Promise<ImportedServiceData[]> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = e.target?.result as ArrayBuffer;
                const workbook = new ExcelJS.Workbook();
                await workbook.xlsx.load(data);

                const worksheet = workbook.worksheets[0];
                if (!worksheet) {
                    resolve([]);
                    return;
                }

                const rows: Record<string, unknown>[] = [];
                let headers: string[] = [];

                worksheet.eachRow((row, rowNumber) => {
                    // ExcelJS row.values is 1-based, [0] is usually undefined
                    const values = row.values as unknown[];
                    if (rowNumber === 1) {
                        headers = values.slice(1).map((v) => String(v ?? '').trim());
                        return;
                    }
                    const obj: Record<string, unknown> = {};
                    headers.forEach((header, idx) => {
                        if (header) {
                            obj[header] = values[idx + 1];
                        }
                    });
                    rows.push(obj);
                });

                const parsedServices = rows
                    .map((row): ImportedServiceData | null => {
                        const name = row['Название'] ?? row['Name'];
                        const category = row['Категория'] ?? row['Category'] ?? 'Разное';
                        const unitRaw = row['Ед. изм.'] ?? row['Unit'] ?? 'pcs';
                        const price = parseSafeNumber(
                            row['Цена'] ?? row['Цена (PLN)'] ?? row['Price'] ?? '0'
                        );

                        if (!name) return null;

                        let unitType = 'pcs';
                        const unitLower = String(unitRaw).toLowerCase().trim();
                        if (UNIT_MAP[unitLower]) {
                            unitType = UNIT_MAP[unitLower];
                        }

                        return {
                            name: String(name).trim(),
                            category: String(category).trim(),
                            unitType,
                            priceLabor: price,
                            adjustment: 1,
                        };
                    })
                    .filter((item): item is ImportedServiceData => item !== null);

                resolve(parsedServices);
            } catch (err) {
                reject(err);
            }
        };
        reader.readAsArrayBuffer(file);
    });
};
